import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import EditorLoadingContext from "../../../components/editorLoadingContext";
import styled from "@emotion/styled";
import ReactToPrint from "react-to-print";
import { Box, Button, CircularProgress, Typography, LinearProgress } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { DragDropContext } from "react-beautiful-dnd";
import inspectionApi from "../../../apis/inspectionApi";
import SystemChoiceDroppable from "./SystemChoiceDroppable";
import ObservationReportGenerator from "../ObservationReportGenerator";
import CoverageDroppable from "./CoverageDroppable";
import { citationsHasNoSytemChoice } from "../../../utilities/helpers";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ieqipCoreServiceApi from "../../../apis/ieqipCoreServiceApi";

const CoverageDnD = ({ coverageDndType, coverageChoiceGuid, systemChoiceField }) => {
  const [showPrintToPdf, setShowPrintToPdf] = useState(false);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const RootStyle = styled("div")({
    flexGrow: 1,
  });
  const reportComponentRef = useRef();
  return (
    <RootStyle>
      {showPrintToPdf && <ReactToPrint
        trigger={() => (
          <Button variant="contained" color="primary" style={{ float: 'right', marginRight: '10px' }} startIcon={<PictureAsPdfIcon />}>
            Print to PDF
          </Button>
        )}
        content={() => reportComponentRef.current}
      />}
      <CoverageDnDPrintable 
        ref={reportComponentRef} 
        coverageDndType={coverageDndType} 
        coverageChoiceGuid={coverageChoiceGuid} 
        systemChoiceField={systemChoiceField} 
        showReportPreview={showReportPreview}
        setShowReportPreview={setShowReportPreview}
        setShowPrintToPdf={setShowPrintToPdf}
      />
    </RootStyle>
  );
};

const CoverageDnDPrintable = React.forwardRef(({ coverageDndType, coverageChoiceGuid, systemChoiceField, showReportPreview, setShowReportPreview, setShowPrintToPdf }, ref) => {
  const [inspection, setInspection] = useState(null);
  const [systemChoices, setSystemChoices] = useState([]);
  const [questionCitations, setQuestionCitations] = useState([]);
  const [b503Operations, setb503Operations] = useState([]);
  const [a503Operations, seta503Operations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [citationsLoading, setCitationsLoading] = useState(false);
  const [detailsLoadedBytes, setDetailsLoadedBytes] = useState(0);
  const [expandedAccordions, setExpandedAccordions] = useState({});

  const { inspectionUid } = useParams();

  // Track TinyMCE editor initialization. The citation rows mount eagerly (even inside
  // collapsed accordions) and each rich-text editor loads from the TinyMCE CDN, which is
  // the slow part long after the API data has arrived. Keep the banner up until every
  // editor that mounted has fired onInit.
  const [editorsLoading, setEditorsLoading] = useState(false);
  const editorsMountedRef = useRef(0);
  const editorsReadyRef = useRef(0);
  const noEditorTimerRef = useRef(null);
  const maxEditorTimerRef = useRef(null);

  const clearEditorTimers = useCallback(() => {
    if (noEditorTimerRef.current) { clearTimeout(noEditorTimerRef.current); noEditorTimerRef.current = null; }
    if (maxEditorTimerRef.current) { clearTimeout(maxEditorTimerRef.current); maxEditorTimerRef.current = null; }
  }, []);

  const handleEditorMount = useCallback(() => {
    editorsMountedRef.current += 1;
    // Editors exist — cancel the "no editors present" fallback.
    if (noEditorTimerRef.current) { clearTimeout(noEditorTimerRef.current); noEditorTimerRef.current = null; }
  }, []);

  const handleEditorReady = useCallback(() => {
    editorsReadyRef.current += 1;
    // All rows mount in one commit before any async TinyMCE init fires, so the mounted
    // count is final by the time the first editor is ready.
    if (editorsMountedRef.current > 0 && editorsReadyRef.current >= editorsMountedRef.current) {
      clearEditorTimers();
      setEditorsLoading(false);
    }
  }, [clearEditorTimers]);

  const editorLoadingCtx = useMemo(
    () => ({ onEditorMount: handleEditorMount, onEditorReady: handleEditorReady }),
    [handleEditorMount, handleEditorReady]
  );

  // Clear any pending editor timers on unmount.
  useEffect(() => () => clearEditorTimers(), [clearEditorTimers]);

  const handleAccordionChange = (accordionId) => (event, isExpanded) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [accordionId]: isExpanded
    }));
  };

  const RootStyle = styled("div")({
    flexGrow: 1,
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      setCitationsLoading(true);
      setDetailsLoadedBytes(0);
      clearEditorTimers();
      setEditorsLoading(false);
      editorsMountedRef.current = 0;
      editorsReadyRef.current = 0;

      // Phase 1: load inspection for system choice headers — renders accordions immediately
      const responseInspection = await inspectionApi.get(`/inspections/${inspectionUid}`);
      const inspectionData = responseInspection.data.body[0];
      setInspection(inspectionData);
      setSystemChoices(inspectionData[systemChoiceField]);
      setLoading(false);

      // Phase 2: load minimal citation data + 503 operations in parallel
      const [responseInspectionDetail] = await Promise.all([
        ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspectiondetails?coverage_choice=${coverageChoiceGuid}&minimal=true`, {
          // Track download bytes so the progress banner can show MB received.
          onDownloadProgress: (e) => setDetailsLoadedBytes(e.loaded || 0),
        }),
        // 503B
        ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspection503boperations`)
          .then(r => setb503Operations(r.data?.body || r.data || []))
          .catch(() => setb503Operations([])),
        // 503A
        ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspection503aoperations`)
          .then(r => seta503Operations(r.data?.body || r.data || []))
          .catch(() => seta503Operations([])),
      ]);

      const citationsData = responseInspectionDetail.data;

      // Initialize sortOrder per group if not already set
      const protocolQualityOrder = new Map();
      (inspectionData.protocol_quality || []).forEach((item, index) => {
        protocolQualityOrder.set(item.inspection_detail_uid, index);
      });

      const citationsByGroup = {};
      citationsData.forEach(citation => {
        const groupKey = `${citation.detail?.systemChoice || 'none'}_${citation.detail?.coverageChoice || 'none'}`;
        if (!citationsByGroup[groupKey]) citationsByGroup[groupKey] = [];
        citationsByGroup[groupKey].push(citation);
      });

      Object.values(citationsByGroup).forEach(groupCitations => {
        groupCitations.sort((a, b) => {
          const orderA = protocolQualityOrder.get(a?.inspection_detail_uid) ?? Number.MAX_SAFE_INTEGER;
          const orderB = protocolQualityOrder.get(b?.inspection_detail_uid) ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
        groupCitations.forEach((citation, index) => {
          if (!citation.detail) citation.detail = {};
          if (citation.detail.sortOrder === undefined || citation.detail.sortOrder === null) {
            citation.detail.sortOrder = index;
          }
        });
      });

      setQuestionCitations(citationsData);

      // Hand off to editor-readiness tracking: the rows (and their TinyMCE editors) are
      // about to render. Keep the banner up until those editors finish initializing.
      editorsMountedRef.current = 0;
      editorsReadyRef.current = 0;
      clearEditorTimers();
      setEditorsLoading(true);
      // If no editor mounts shortly, there are none to wait for — stop the banner.
      noEditorTimerRef.current = setTimeout(() => setEditorsLoading(false), 1500);
      // Safety net so the banner can never get stuck if an editor fails to init.
      maxEditorTimerRef.current = setTimeout(() => setEditorsLoading(false), 30000);

      setCitationsLoading(false);
    })();
  }, [inspectionUid, systemChoiceField, coverageChoiceGuid, clearEditorTimers]);

  const onDragEnd = async (result) => {
    const { destination, source, draggableId, type } = result;
    if (!destination) return; // drop to outside droppable context
    if (destination.droppableId === source.droppableId && destination.index === source.index) return; // user drop back to start position

    if (type?.toLowerCase() === "systemChoice".toLowerCase()) {
      // Update draggable destination
      const newSystemChoices = Array.from(systemChoices);
      newSystemChoices.splice(source.index, 1); // remove source from array
      const newSystemChoice = systemChoices.find((systemChoice) => systemChoice?.code_table_uid === draggableId);
      newSystemChoices.splice(destination.index, 0, newSystemChoice); // insert destination to array

      // Update state
      setSystemChoices(newSystemChoices);

      // Save new systemChoice of inspection to database
      inspection[systemChoiceField] = newSystemChoices;
    } else if (type?.toLowerCase().startsWith("discussion") || type?.toLowerCase().startsWith("observation")) {
      const typeLower = type?.toLowerCase();
      const is503B = typeLower.endsWith("-503b");
      const is503A = typeLower.endsWith("-503a");

      if (is503B || is503A) {
        const operations = is503B ? b503Operations : a503Operations;
        const setOperations = is503B ? setb503Operations : seta503Operations;
        const uidField = is503B ? 'inspection_503b_operation_uid' : 'inspection_503a_operation_uid';
        const endpoint = is503B
          ? '/api/inspections/inspection503boperations/sortorder'
          : '/api/inspections/inspection503aoperations/sortorder';

        const filtered = operations
          .filter(op => op.detail?.coverageChoice === coverageChoiceGuid)
          .sort((a, b) => (a.detail?.sortOrder ?? 0) - (b.detail?.sortOrder ?? 0));

        const reordered = Array.from(filtered);
        const [movedItem] = reordered.splice(source.index, 1);
        reordered.splice(destination.index, 0, movedItem);

        const reorderedWithOrder = reordered.map((op, idx) => ({
          ...op,
          detail: { ...op.detail, sortOrder: idx },
        }));

        const otherOps = operations.filter(op => op.detail?.coverageChoice !== coverageChoiceGuid);
        setOperations([...otherOps, ...reorderedWithOrder]);

        try {
          const sortOrderPayload = reorderedWithOrder.map((op, idx) => ({
            uid: op[uidField],
            sort_order: idx,
          }));
          await ieqipCoreServiceApi.put(endpoint, sortOrderPayload);
          console.log(`Successfully saved sort order for ${reorderedWithOrder.length} ${is503B ? '503B' : '503A'} operations`);
        } catch (error) {
          console.error('Error saving 503 operation sort order:', error);
        }
        return;
      }

      // Find the dragging questionCitation
      const foundQuestionCitation = questionCitations.find((questionCitation) => questionCitation?.code === draggableId);
      
      if (!foundQuestionCitation) {
        console.error('Could not find dragged citation:', draggableId);
        return;
      }

      // Filter all questionCitations of found questionCitation's system choice AND coverage choice
      const questionCitationsBySystemChoiceAndCoverage = questionCitations.filter(
        (questionCitation) => 
          questionCitation?.detail.systemChoice === foundQuestionCitation.detail.systemChoice && 
          questionCitation?.detail.coverageChoice === coverageChoiceGuid
      );

      // Sort by current sortOrder to ensure we have the correct initial order
      questionCitationsBySystemChoiceAndCoverage.sort((a, b) => {
        const orderA = a?.detail?.sortOrder ?? 0;
        const orderB = b?.detail?.sortOrder ?? 0;
        return orderA - orderB;
      });

      // Perform reorder within this specific system choice/coverage combination
      const reorderedCitations = Array.from(questionCitationsBySystemChoiceAndCoverage);
      const [movedItem] = reorderedCitations.splice(source.index, 1);
      reorderedCitations.splice(destination.index, 0, movedItem);

      // Create new citation objects with updated sortOrder to ensure React detects changes
      const reorderedCitationsWithNewOrder = reorderedCitations.map((citation, idx) => ({
        ...citation,
        detail: {
          ...citation.detail,
          sortOrder: idx
        }
      }));

      // Update the questionCitations state by replacing only the affected citations
      const otherQuestionCitations = questionCitations.filter(
        (questionCitation) => 
          !(questionCitation?.detail.systemChoice === foundQuestionCitation.detail.systemChoice && 
            questionCitation?.detail.coverageChoice === coverageChoiceGuid)
      );
      
      const updatedQuestionCitations = [...otherQuestionCitations, ...reorderedCitationsWithNewOrder];
      
      const uniqueQuestionCitations = updatedQuestionCitations.filter((questionCitation, index, self) => 
        index === self.findIndex(item => item.inspection_detail_uid === questionCitation.inspection_detail_uid)
      );
      
      setQuestionCitations(uniqueQuestionCitations);

      try {
        await Promise.all(
          reorderedCitationsWithNewOrder.map((citation) =>
            ieqipCoreServiceApi.patch(
              `/api/inspections/inspectiondetails/${citation.inspection_detail_uid}/detail`,
              { sortOrder: citation.detail.sortOrder }
            )
          )
        );

        console.log('Successfully saved sort order for', reorderedCitationsWithNewOrder.length, 'citations');
      } catch (error) {
        console.error('Error saving sort order:', error);
      }
      
      return;
    }
    await inspectionApi.put(`/inspections/${inspection.inspection_uid}`, inspection);
  };

  const foundCitationHasNoSystemChoice = citationsHasNoSytemChoice(questionCitations);

  const handlePrintToPdf = () => {
    setShowReportPreview(true);
    setShowPrintToPdf(true);
  }

  return (
    <EditorLoadingContext.Provider value={editorLoadingCtx}>
    <RootStyle ref={ref}>
      <style type="text/css" media="print">
        {
          "\
          @media all {\
          .page-break {\
            display: none;\
          }\
        }\
        \
        @media print {\
          html, body {\
            height: auto !important;\
            overflow: visible !important;\
            -webkit-print-color-adjust: exact;\
          }\
          .no-print {\
            display: none !important;\
          }\
        }\
        \
        @media print {\
          .page-break {\
            margin-top: 1rem;\
            display: block;\
            page-break-before: always;\
          }\
        }\
        \
        @page {\
          size: auto;\
          margin-left: 10mm;\
          margin-right: 10mm;\
          margin-top: 20mm;\
          margin-bottom: 20mm;\
        }\
      "
        }
      </style>
      <DragDropContext onDragEnd={onDragEnd}>
        {/* Sticky progress banner — stays until everything is loaded AND rendered:
            inspection (phase 1), citations/503 operations (phase 2), and finally every
            TinyMCE rich-text editor in the rows finishing its init. */}
        {!showReportPreview && (loading || citationsLoading || editorsLoading) && (
          <Box
            className="no-print"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1200,
              backgroundColor: '#1565c0',
              color: '#fff',
              px: 3,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress size={20} sx={{ color: '#fff', flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {loading ? (
                  <>
                    Loading {coverageDndType} report&hellip;
                    <span style={{ fontWeight: 400, marginLeft: 8 }}>preparing sections</span>
                  </>
                ) : citationsLoading ? (
                  <>
                    Downloading {coverageDndType} data&hellip;
                    {detailsLoadedBytes > 0 && (
                      <span style={{ fontWeight: 400, marginLeft: 8 }}>
                        {(detailsLoadedBytes / 1048576).toFixed(1)} MB received — this report is large, please wait
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    Preparing report editors&hellip;
                    <span style={{ fontWeight: 400, marginLeft: 8 }}>rendering sections, almost ready</span>
                  </>
                )}
              </Typography>
              <LinearProgress sx={{ mt: 0.75, borderRadius: 1, backgroundColor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { backgroundColor: '#fff' } }} />
            </Box>
          </Box>
        )}
        {!showReportPreview && (
          <Box m="10px">
            <Button variant="contained" color="primary" startIcon={<PrintIcon />} onClick={handlePrintToPdf}>
              Print {coverageDndType}
            </Button>
          </Box>
        )}

        {!showReportPreview && (
          <Box m="10px">
            {!foundCitationHasNoSystemChoice && <SystemChoiceDroppable systemChoices={systemChoices} questionCitations={questionCitations} b503Operations={b503Operations} a503Operations={a503Operations} coverageDndType={coverageDndType} coverageChoice={coverageChoiceGuid} inspection={inspection} expandedAccordions={expandedAccordions} onAccordionChange={handleAccordionChange} citationsLoading={citationsLoading} />}
            {foundCitationHasNoSystemChoice && <CoverageDroppable questionCitationsPerSystemChoice={questionCitations?.filter((p) => p.detail?.coverageChoice === coverageChoiceGuid)} coverageDndType={coverageDndType} coverageChoice={coverageChoiceGuid} inspection={inspection} />}
          </Box>
        )}
        {showReportPreview && (
          <Box>
            <ObservationReportGenerator reportType={coverageDndType} setShowReportPreview={setShowReportPreview} setShowPrintToPdf={setShowPrintToPdf}/>
          </Box>
        )}
      </DragDropContext>
    </RootStyle>
    </EditorLoadingContext.Provider>
  );
});

export default CoverageDnD;
